import {
  Box,
  chakra,
  Checkbox,
  keyframes,
  Spinner,
  Stack,
  Tag,
  TagLabel,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import Header from '../../../../components/Header/Header';
import CardHeaderMobile from '../../../../components/Mobile/header';
import { IDetailSelectiveProcess } from '../../../../services/hooks/SelectiveProcess/Types';
import { useState } from 'react';
import { MdOutlineRuleFolder } from 'react-icons/md';
import { FieldValues } from 'react-hook-form';
import MessageError from '../../../../components/MessageError';
import { useNavigate } from 'react-router-dom';
import NavigationButtonMobilePage from '../../../../components/Mobile/navigationButton';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

interface IOwnProps {
  data?: IDetailSelectiveProcess;
  isLoadingSP: boolean;
  onSubmit(data: FieldValues): void;
}

const NoticeMobilePage: React.FC<IOwnProps> = ({ data, isLoadingSP, onSubmit }) => {
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(false);

  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const navigate = useNavigate();

  const handleValidateAccept = () => {
    if (accepted) {
      setError(false);
    } else {
      setError(true);
      return;
    }

    onSubmit({ terms: accepted });
  };

  return (
    <>
      <Stack h="calc(100dvh - 30px)" top={0} w="100%" bg="white" gap={0}>
        <Header />

        <VStack mt={0} flex={1} py={2} overflow="hidden" minH={0} w="100%">
          {(isLoadingSP && (
            <VStack flex={1} w="100%" justify="center" minH={0}>
              <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
              <Text fontSize="sm">Buscando dados do processo seletivo...</Text>
            </VStack>
          )) || (
            <AnimatedBox
              bgGradient={bgGradient}
              borderRadius="lg"
              boxShadow="lg"
              mx={3}
              p={{ base: 5, md: 10 }}
              borderWidth="1px"
              borderColor={borderColor}
              bg="white"
              flex={1}
              display="flex"
              flexDir="column"
              minH={0}
            >
              <VStack boxSizing="border-box" maxW="lg" spacing={4} align="stretch" flex={1} minH={0} pb={6}>
                <CardHeaderMobile
                  title="Termos e condições"
                  subTitle="Para prosseguir, é necessário ler e aceitar os termos e condições de uso do edital desse processo seletivo."
                  badgeText="Regras"
                  badgeIcon={<MdOutlineRuleFolder size={14} />}
                  px={3}
                  gap={1}
                  colorScheme="cyan"
                />

                <Text
                  bgColor="primaryColor"
                  color="white"
                  boxSizing="border-box"
                  py={1}
                  px={5}
                  fontWeight="bold"
                  fontSize="small"
                  textTransform="uppercase"
                >
                  {data?.name || '--'}
                </Text>

                <VStack spacing={3} align="stretch" flex={1} minH={0}>
                  <Tag size="sm" w="fit-content" variant="outline" colorScheme="purple">
                    <TagLabel>{data?.selective_process_type || ''}</TagLabel>
                  </Tag>
                  <Box
                    flex={1}
                    minH={0}
                    border="1px"
                    borderColor="gray.200"
                    color="black"
                    borderRadius="sm"
                    overflowY="auto"
                    p={3}
                  >
                    <Box
                      as="div"
                      fontSize="sm"
                      lineHeight="tall"
                      sx={{
                        '& h1, & h2, & h3': { fontWeight: 600, mt: 3, mb: 2 },
                        '& p': { mb: 3 },
                        '& ul, & ol': { pl: 5, mb: 3 },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: data?.terms_of_use || 'Declaro ter lido e concordado com os termos do edital.',
                      }}
                    />
                  </Box>
                  <Box>
                    <Checkbox isChecked={accepted} onChange={(e) => setAccepted(e.target.checked)} colorScheme="green">
                      Li e concordo com os termos de uso
                    </Checkbox>
                    {error && (
                      <MessageError fontSize="x-small">
                        Para continuar é preciso aceitar os termos e condições do processo seletivo
                      </MessageError>
                    )}
                  </Box>
                </VStack>
              </VStack>
            </AnimatedBox>
          )}
        </VStack>
      </Stack>
      <NavigationButtonMobilePage
        absolute
        mainButtonText="Continuar"
        onGoMainButton={handleValidateAccept}
        mainButtonDisabled={!accepted}
        goBackButtonText="Voltar"
        onGoBackButton={() => navigate(-1)}
      />
    </>
  );
};

export default NoticeMobilePage;
